import type {DataAuditActions} from 'src/action-creators/data-audit';
import type {
  DataAuditDashboardCard,
  DataAuditEntityType,
  DataAuditAuditTableState,
  DataAuditAuditDetailState,
  DataAuditAuditByAtsIdState,
  DataAuditQueuedTableState,
  DataAuditSupportedFieldApi,
  DataAuditWritebackBreakdownGraphsState,
  DataAuditWritebackType,
} from 'src/components/data-audit/types';

import {
  CLEAR_AUDIT_BY_ATS_ID,
  CLEAR_AUDIT_DETAIL,
  RECEIVE_AUDIT_BY_ATS_ID,
  RECEIVE_AUDIT_DETAIL,
  RECEIVE_AUDIT_TABLE_ENTRIES,
  RECEIVE_DASHBOARD_CARDS,
  RECEIVE_ENTITY_TYPES,
  RECEIVE_NOTE_TYPES,
  RECEIVE_QUEUED_TABLE_ENTRIES,
  RECEIVE_SUPPORTED_FIELDS,
  RECEIVE_WRITEBACK_AUDIT_STATUSES,
  RECEIVE_WRITEBACK_BREAKDOWN_GRAPHS,
} from 'src/action-creators/data-audit';


export type DataAuditState = {
  dashboardCardStats: DataAuditDashboardCard;
  writebackBreakdownGraphs: DataAuditWritebackBreakdownGraphsState;
  auditStatusesByWritebackType: Partial<
    Record<DataAuditWritebackType, Array<string>>
  >;
  entityTypes: Array<DataAuditEntityType>;
  noteTypes: Array<string>;
  auditTable: DataAuditAuditTableState;
  auditDetail: DataAuditAuditDetailState;
  auditByAtsId: DataAuditAuditByAtsIdState;
  queuedTable: DataAuditQueuedTableState;
  supportedFields: Array<DataAuditSupportedFieldApi>;
};

const initialAuditTableState: DataAuditAuditTableState = {
  totalElements: 0,
  entries: [],
  params: null,
};

const initialAuditDetailState: DataAuditAuditDetailState = {
  detail: null,
  auditId: null,
  writebackType: null,
};

const initialAuditByAtsIdState: DataAuditAuditByAtsIdState = {
  nodes: [],
  atsId: null,
  writebackType: null,
};

const initialQueuedTableState: DataAuditQueuedTableState = {
  totalElements: 0,
  entries: [],
  params: null,
};

const initialWritebackBreakdownGraphs: DataAuditWritebackBreakdownGraphsState =
  {
    nwb: null,
    owb: null,
    fwb: null,
  };

const initialState: DataAuditState = {
  dashboardCardStats: {
    totalWritebacks: 0,
    successRate: 0,
    failureCount: 0,
    currentlyInQueue: 0,
  },
  writebackBreakdownGraphs: initialWritebackBreakdownGraphs,
  auditStatusesByWritebackType: {},
  entityTypes: [],
  noteTypes: [],
  auditTable: initialAuditTableState,
  auditDetail: initialAuditDetailState,
  auditByAtsId: initialAuditByAtsIdState,
  queuedTable: initialQueuedTableState,
  supportedFields: [],
};

export default (
  state: DataAuditState = initialState,
  action: DataAuditActions,
): DataAuditState => {
  switch (action.type) {
    case RECEIVE_DASHBOARD_CARDS: {
      return {
        ...state,
        dashboardCardStats: action.payload,
      };
    }
    case RECEIVE_WRITEBACK_AUDIT_STATUSES: {
      const {writebackType, statuses} = action.payload;

      return {
        ...state,
        auditStatusesByWritebackType: {
          ...state.auditStatusesByWritebackType,
          [writebackType]: statuses,
        },
      };
    }
    case RECEIVE_ENTITY_TYPES: {
      return {
        ...state,
        entityTypes: action.payload ?? [],
      };
    }
    case RECEIVE_NOTE_TYPES: {
      return {
        ...state,
        noteTypes: action.payload ?? [],
      };
    }
    case RECEIVE_AUDIT_TABLE_ENTRIES: {
      const {response, params} = action.payload;

      return {
        ...state,
        auditTable: {
          totalElements: response.totalElements ?? 0,
          entries: response.responses ?? [],
          params,
        },
      };
    }
    case RECEIVE_AUDIT_DETAIL: {
      const {detail, auditId, writebackType} = action.payload;

      return {
        ...state,
        auditDetail: {
          detail,
          auditId,
          writebackType,
        },
      };
    }
    case CLEAR_AUDIT_DETAIL: {
      return {
        ...state,
        auditDetail: initialAuditDetailState,
      };
    }
    case RECEIVE_AUDIT_BY_ATS_ID: {
      const {nodes, atsId, writebackType} = action.payload;

      return {
        ...state,
        auditByAtsId: {
          nodes,
          atsId,
          writebackType,
        },
      };
    }
    case CLEAR_AUDIT_BY_ATS_ID: {
      return {
        ...state,
        auditByAtsId: initialAuditByAtsIdState,
      };
    }
    case RECEIVE_QUEUED_TABLE_ENTRIES: {
      const {response, params} = action.payload;

      return {
        ...state,
        queuedTable: {
          totalElements: response.totalElements ?? 0,
          entries: response.responses ?? [],
          params,
        },
      };
    }
    case RECEIVE_WRITEBACK_BREAKDOWN_GRAPHS: {
      return {
        ...state,
        writebackBreakdownGraphs: action.payload,
      };
    }
    case RECEIVE_SUPPORTED_FIELDS: {
      return {
        ...state,
        supportedFields: action.payload ?? [],
      };
    }
  }

  return state;
};
